import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { forwardRef, useState, useEffect, useMemo, createContext, useContext } from 'react';
import { useId } from '@reach/auto-id';
import { Box } from '../Box/index.js';
import { useAccordionContext } from './Accordion.js';
import { getCardStyles } from '../Card/Card.js';

/* ======================================= */
const AccordionItemContext = /*#__PURE__*/createContext({});
const useAccordionItemContext = () => useContext(AccordionItemContext);
/* ======================================= */
/**
 * @deprecated Please use AccordionItemAlpha instead of AccordionItem moving forward. AccordionItem will be deprecated in future major releases.
 *
 * ---
 * Accordion allows users to expand one or more panels at a time, revealing information
within a small space.
 *
 * _Note_: AccordionHeader and AccordionPanels have default padding of 'lg', which can be overridden.
 *
 * ### Documentation
 * - [Examples](https://blockparty.endpoint.com/components/accordion#usage)
 * - [Props](https://blockparty.endpoint.com/components/accordion#props)
 * <br />
 *
 */
const AccordionItem = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    defaultIsOpen = false,
    id,
    isCollapsed,
    isDisabled,
    variant = 'outline',
    children,
    ...rest
  } = _ref;
  const [collapsed, setCollapsed] = useState(isCollapsed);
  const {
    openAll
  } = useAccordionContext();
  const accordionStyles = getCardStyles(variant);
  // Effect for openAll
  useEffect(() => {
    setCollapsed(!openAll);
  }, [openAll]);
  // Effect for defaultIsOpen
  useEffect(() => {
    if (!defaultIsOpen) {
      // Revisit: Name convention does not match up with logic due to `openAll` initial state
      setCollapsed(false);
    }
  }, [defaultIsOpen]);
  /* ======================================= */
  const uuid = useId();
  const _id = id || uuid;
  const headerId = `accordion-header-${_id}`;
  const panelId = `accordion-panel-${_id}`;
  const context = useMemo(() => ({
    collapsed,
    setCollapsed,
    isDisabled,
    headerId,
    panelId
  }), [collapsed, setCollapsed, isDisabled, headerId, panelId]);
  return /*#__PURE__*/React__default.createElement(AccordionItemContext.Provider, {
    value: context
  }, /*#__PURE__*/React__default.createElement(Box, _extends({
    ref: ref,
    borderRadius: "radiusDefault"
  }, accordionStyles, rest), children));
});
AccordionItem.displayName = 'AccordionItem';

export { AccordionItem, useAccordionItemContext };
